import { useEffect, useState } from 'react';
import {
  IconAngleDoubleLeft,
  IconAngleDoubleRight,
  IconExternalLink,
} from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { t, Trans } from '@lingui/macro';
import clsx from 'clsx';
import { ProgressBarWrapper } from 'components/ProgressBar/ProgressBarWrapper';
import { useRouterApplicationId } from 'components/Signup/hooks/useRouterApplicationId';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { usePathname } from 'hooks/usePathname/usePathname';
import useWindowDimensions from 'hooks/useWindowDimensions';
import {
  TApplicationOverview,
  TIntroducerApplication,
} from 'lendingApi/applications.types';
import {
  useGetIntroducerApplication,
  useGetStateQuery,
} from 'lendingApi/lendingApiHooks';
import { Link } from 'react-router-dom';
import { IwQueryClient } from 'store/IwQueryClient';
import { APPLICATION_STATUS } from 'utils/applications/applications.config';
import { BREAK_S } from 'utils/breakpoints';
import { getEnvironment } from 'utils/environmentHelper/environmentHelper';
import {
  APPLICATION_DOCUMENTS_PATH,
  NEW_APPLICATIONS_PATH,
  PATH_NAMES,
} from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { GuarantorsList } from './GuarantorsList/GuarantorsList';
import styles from './SideNavigation.module.css';
import { SideNavigationDetail } from './SideNavigationDetail/SideNavigationDetail';

type TProps = {
  documentRequirementsIsFetching?: boolean;
  hideSideNav?: boolean;
  onSaveForLater?: () => void;
  saveForLaterDisabled?: boolean;
  saveForLaterLoading?: boolean;
};

export const SideNavigation = ({
  hideSideNav,
  onSaveForLater,
  saveForLaterDisabled = false,
  saveForLaterLoading = false,
}: TProps) => {
  const pathname = usePathname();
  const fullPathname = usePathname({ fullPath: true });
  const { isUK } = useLocale();
  const { trackEvent } = useAmplitude();
  const { application } = useGetIntroducerApplication();
  const { state, stateStatus, stateFetching } = useGetStateQuery();
  const { width } = useWindowDimensions();
  const { splitTests } = useIntroducerContext();
  const [isNavCollapsed, setIsNavCollapsed] = useState(width <= BREAK_S);

  const [applicationOverview, setApplicationOverview] =
    useState<TApplicationOverview>({
      companyName: '',
      companyNumber: '',
      applicantName: '',
    });

  const stateHasLoaded = state && stateStatus === 'success';
  const { companyName, companyNumber, applicantName } = applicationOverview;
  const applicationId = useRouterApplicationId()!;
  const applicationLink =
    pathname === NEW_APPLICATIONS_PATH && state?.state_key
      ? PATH_NAMES.newApplicationStep1({ applicationId })
      : PATH_NAMES.application({ applicationId });

  const companyInfoUrl = `https://find-and-update.company-information.service.gov.uk/company/${companyNumber}`;
  const showAdditionalGuarantors =
    application?.status &&
    [
      APPLICATION_STATUS.offered,
      APPLICATION_STATUS.conditional_offer,
      APPLICATION_STATUS.talking_to_customer,
    ].includes(application?.status);

  useEffect(() => {
    if (application) {
      setApplicationOverview({
        companyName: application.company?.registered_company_name,
        companyNumber: application.company?.company_number,
        applicantName: `${application.customer?.first_name} ${application.customer?.last_name}`,
      });
    }
  }, [application, state, stateHasLoaded]);

  useEffect(() => {
    if (stateHasLoaded) {
      const applicant = state.application?.people?.find((person) =>
        person.roles?.includes('applicant'),
      );
      setApplicationOverview({
        companyName: state.application?.company?.registered_company_name,
        companyNumber: state.application?.company?.company_number,
        applicantName: `${applicant?.first_name || ''} ${
          applicant?.last_name || ''
        }`,
      });
    }
  }, [state, stateHasLoaded]);

  const isNewSignup = pathname?.includes(NEW_APPLICATIONS_PATH);
  const applicationStatus = application?.status;

  const eligibleDocumentUploadStatus: TIntroducerApplication['status'][] = [
    'open',
    'action_required',
    'customer_to_link_open_banking',
    'awaiting_underwriter',
    'in_underwriting',
    'manual_review',
  ];

  const showDocumentsTab =
    applicationStatus &&
    eligibleDocumentUploadStatus.includes(applicationStatus);

  const isCollapsable = width <= BREAK_S;

  const handleSaveForLater = () => {
    if (!onSaveForLater) {
      return;
    }

    IwQueryClient.getOrCreate().invalidateQueries({
      queryKey: ['applications'],
    });

    trackEvent(AMPLITUDE_EVENTS.saveAndFinishLaterClicked, {
      'CTA text': 'Save & finish later',
      Reintroduction: Boolean(
        state?.ui?.reintroduction &&
          splitTests?.sameIntroducerAccountReintroductions,
      ),
    });

    onSaveForLater();
  };

  return (
    <aside
      data-testid="side-navigation"
      className={clsx(styles.sideNavigation, {
        [styles.hideSideNavigation]:
          hideSideNav || (isNavCollapsed && isCollapsable),
        [styles.isStaging]: getEnvironment() === 'staging',
      })}
    >
      {fullPathname !== PATH_NAMES.newApplicationStep0() &&
        isCollapsable &&
        (isNavCollapsed ? (
          <Button
            data-testid="expand-button"
            variant="denim"
            className={styles.sideNavigationExpand}
            onClick={() => setIsNavCollapsed(false)}
          >
            <IconAngleDoubleRight height={24} width={24} />
          </Button>
        ) : (
          <Button
            data-testid="collapse-button"
            variant="tertiary"
            className={styles.sideNavigationCollapse}
            onClick={() => setIsNavCollapsed(true)}
          >
            <IconAngleDoubleLeft height={24} width={24} />
          </Button>
        ))}
      <section>
        <div className={styles.companyInfo}>
          <SideNavigationDetail
            className={styles.companyName}
            isLoading={stateFetching}
            value={companyName}
            spinnerTestId="company-name-spinner"
          />

          {isUK && (
            <SideNavigationDetail
              label={t`Company number`}
              isLoading={stateFetching}
              value={companyNumber}
              spinnerTestId="company-number-spinner"
              actionButton={
                <Button
                  data-testid="company-info-external-link"
                  variant="tertiary"
                  iconOnly
                  className={styles.externalLink}
                  href={companyInfoUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() =>
                    trackEvent(AMPLITUDE_EVENTS.externalLinkClicked, {
                      'CTA destination': 'Companies house',
                      'CTA text': '',
                      'CTA URL': companyInfoUrl,
                    })
                  }
                >
                  <IconExternalLink />
                </Button>
              }
            />
          )}

          <SideNavigationDetail
            label={t`Applicant`}
            isLoading={stateFetching}
            value={applicantName?.trim()}
            spinnerTestId="customer-name-spinner"
          />

          {showAdditionalGuarantors && (
            <GuarantorsList
              isLoading={stateFetching}
              guarantors={state?.application?.people}
            />
          )}
        </div>
        <ul className={styles.navList}>
          <li className={styles.navListItem}>
            <Link
              to={applicationLink}
              className={clsx(styles.navLink, {
                [styles.navLinkSelected]:
                  pathname === '/application' || isNewSignup,
                [styles.navLinkDisabled]: isNewSignup,
              })}
            >
              <Trans>Application</Trans>
            </Link>
          </li>
          {!isNewSignup && showDocumentsTab && (
            <li className={styles.navListItem}>
              <Link
                to={PATH_NAMES.applicationDocuments({ applicationId })}
                className={clsx(styles.navLink, {
                  [styles.navLinkSelected]:
                    pathname === APPLICATION_DOCUMENTS_PATH,
                })}
              >
                <Trans>Documents</Trans>
              </Link>
            </li>
          )}
        </ul>
      </section>
      {pathname === NEW_APPLICATIONS_PATH && onSaveForLater && (
        <section className={styles.saveForLater}>
          <Button
            data-testid="save-finish-later-button"
            variant="secondary-alt"
            disabled={saveForLaterDisabled}
            loading={saveForLaterLoading}
            onClick={handleSaveForLater}
          >
            <Trans>Save & finish later</Trans>
          </Button>
        </section>
      )}
      {!pathname.includes(NEW_APPLICATIONS_PATH) && (
        <section>
          <ProgressBarWrapper
            className={styles.progressBar}
            status={applicationStatus}
          />
        </section>
      )}
    </aside>
  );
};
